import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { CalendarDays, ExternalLink, Link2, Loader2, RotateCcw, Trophy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { BracketBoard } from '@/components/bracket/BracketBoard';
import { buildBracketModel, describeEvent } from '@/lib/bracket/model';
import { oapi } from '@/lib/officialsApi';
import { isCricket } from '@/lib/cricket';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Banner = ({ ev, isPro, shared, onShare, onUnlink }) => (
  <div className="rounded-2xl border-2 border-[#00247D]/20 bg-white p-4 sm:p-5 mb-4 flex flex-col lg:flex-row lg:items-center gap-4 animate-in fade-in duration-300" data-testid="managed-bracket-banner">
    <div className="flex items-start gap-3 min-w-0 flex-1">
      <div className="w-10 h-10 rounded-full bg-[#00247D] flex items-center justify-center shrink-0"><Trophy className="w-5 h-5 text-[#F5C542]" /></div>
      <div className="min-w-0">
        <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#CF142B]">Managed tournament · official results</p>
        <h2 className="text-2xl font-black text-[#00247D] leading-tight truncate" style={{ fontFamily: 'Barlow Condensed' }} data-testid="managed-bracket-name">{ev.name}</h2>
        <p className="text-xs text-slate-500">
          {isPro ? 'Click a game to enter or correct its score. It updates the tournament and winners advance.' : 'Scores, stats and schedule are managed in My Schedules.'}
        </p>
      </div>
    </div>
    <div className="flex flex-wrap gap-2">
      <Link to={`/schedule/${ev.id}`}><Button className="bg-[#00247D] hover:bg-[#001A5C]" data-testid="managed-open-schedule-btn"><CalendarDays className="w-4 h-4 mr-1.5" />Open in My Schedules</Button></Link>
      <Link to={`/bracket/${ev.id}`}><Button variant="outline" data-testid="managed-open-bracket-btn"><ExternalLink className="w-4 h-4 mr-1.5" />Bracket view</Button></Link>
      <Button variant="outline" onClick={onShare} data-testid="managed-share-btn"><Link2 className="w-4 h-4 mr-1.5" />{shared ? 'Share link' : 'Get share link'}</Button>
      <Button variant="ghost" onClick={onUnlink} className="text-slate-500" data-testid="managed-new-bracket-btn"><RotateCcw className="w-4 h-4 mr-1.5" />Start a new bracket</Button>
    </div>
  </div>
);

// A Library bracket that became a tournament shows (and scores) the official games.
export const ManagedBracket = React.forwardRef(({ eventId, isPro, shared, onShare, onUnlink }, boardRef) => {
  const [ev, setEv] = useState(null);
  const [gone, setGone] = useState(false);
  const load = useCallback(() => oapi(`/events/${eventId}`).then(setEv).catch((e) => {
    if ([403, 404].includes(e.status)) setGone(true); else toast.error('Could not load the tournament', { description: e.message });
  }), [eventId]);
  useEffect(() => { load(); }, [load]);
  const model = useMemo(() => (ev ? buildBracketModel({ format: ev.format, schedule: ev.schedule }) : null), [ev]);

  const save = async (matchId, a, b, extra) => {
    try {
      await oapi(`/events/${eventId}/schedule/${matchId}`, { method: 'PUT', body: { score_a: a, score_b: b, ...extra } });
      if (a === b) toast.warning('Score saved. It is a tie, so no team advanced.'); else toast.success('Official score saved. Bracket updated.');
      await load();
      return true;
    } catch (e) { toast.error(e.message || 'Failed to save score'); return false; }
  };

  if (gone) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center" data-testid="managed-bracket-gone">
        <p className="text-slate-600">The tournament made from this bracket was deleted or is no longer yours.</p>
        <Button className="mt-4 bg-[#00247D] hover:bg-[#001A5C]" onClick={onUnlink} data-testid="managed-gone-new-btn">Start a new bracket</Button>
      </div>
    );
  }
  if (!model) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#00247D]" /></div>;
  return (
    <div data-testid="managed-bracket">
      <Banner ev={ev} isPro={isPro} shared={shared} onShare={onShare} onUnlink={onUnlink} />
      <BracketBoard ref={boardRef} model={model} title={ev.name?.toUpperCase()} subtitle={describeEvent(ev)}
        eventLogo={ev.event_logo ? `${API_URL}/api/files/${ev.event_logo}` : null} logos={ev.team_logos || {}}
        editable={isPro} onScoreSave={save} storageKey={`bracket-layout:${ev.id}`} fileName={`${(ev.name || 'bracket').toLowerCase().replace(/[^a-z0-9]+/g, '-')}-bracket`}
        showCourt={(ev.num_courts || 1) > 1} cricket={isCricket(ev.sport)} />
    </div>
  );
});

ManagedBracket.displayName = 'ManagedBracket';
